import { Link } from "react-router-dom";
import { motion } from "framer-motion";

const ACCENTS = [
  { bg: "bg-[#0d0d0d]", bar: "bg-maasai", text: "text-maasai" },
  { bg: "bg-[#0b0b0b]", bar: "bg-ochre", text: "text-ochre" },
  { bg: "bg-[#141414]", bar: "bg-maasai", text: "text-maasai" },
  { bg: "bg-[#120404]", bar: "bg-ochre", text: "text-ochre" },
];


export const cleanName = (name = "") =>
  name.replace(/\s*\((Hall of Fame|Europe|USA|Ones to Watch)\)\s*$/i, "").trim();

const POS_LABEL = { GK: "Goalkeeper", DEF: "Defender", MID: "Midfielder", FWD: "Forward" };

export const initials = (name = "") =>
  name
    .replace(/['"]/g, "")
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase())
    .join("");

export const PlayerCard = ({ player, index = 0, showRank = false }) => {
  const accent = ACCENTS[index % ACCENTS.length];
  return (
    <motion.div
      initial={{ opacity: 0, y: 26 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-40px" }}
      transition={{ duration: 0.6, delay: Math.min(index * 0.05, 0.4), ease: [0.16, 1, 0.3, 1] }}
    >
      <Link
        to={`/hall-of-fame/${player.slug}`}
        data-testid={`player-card-${player.slug}`}
        className={`group relative flex h-[300px] flex-col justify-between overflow-hidden border border-white/10 ${accent.bg} p-6 transition-colors duration-500 hover:border-maasai`}
      >
        {player.portrait_url ? (
          <img
            src={player.portrait_url}
            alt=""
            aria-hidden="true"
            loading="lazy"
            className="pointer-events-none absolute inset-0 h-full w-full object-cover object-top opacity-60 grayscale transition-all duration-700 group-hover:scale-105 group-hover:opacity-80 group-hover:grayscale-0"
          />
        ) : (
          <span
            aria-hidden="true"
            className="pointer-events-none absolute -right-4 -top-10 select-none font-display text-[9rem] font-bold leading-none text-white/[0.06] transition-transform duration-700 group-hover:scale-110"
          >
            {initials(cleanName(player.name))}
          </span>
        )}
        <span
          aria-hidden="true"
          className="pointer-events-none absolute inset-0 bg-gradient-to-t from-black via-black/70 to-black/20"
        />

        <div className="relative flex items-start justify-between">
          <p className={`text-[0.62rem] font-bold uppercase tracking-[0.22em] ${accent.text}`}>
            {POS_LABEL[player.position] || player.position}
          </p>
          {showRank && player.rank ? (
            <span
              className="flex h-9 w-9 items-center justify-center bg-maasai font-display text-base font-bold text-white"
              data-testid={`player-rank-${player.slug}`}
            >
              {String(player.rank).padStart(2, "0")}
            </span>
          ) : null}
        </div>

        <div className="relative">
          <span className={`block h-1 w-10 ${accent.bar} transition-all duration-500 group-hover:w-20`} />
          <h3 className="mt-4 font-display text-2xl font-semibold uppercase leading-[0.95] tracking-tight text-white">
            {cleanName(player.name)}
          </h3>
          <p className="mt-2 text-xs text-white/60">{player.club || "—"}</p>
          <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1 border-t border-white/10 pt-3 text-[0.66rem] uppercase tracking-wider text-white/50">
            <span>{player.league || "—"}</span>
            <span>{player.year || player.region}</span>
            {player.total_score ? <span className="text-ochre">Score {player.total_score}</span> : null}
          </div>
          {player.value_usd ? (
            <p
              className="mono mt-2 text-[0.62rem] uppercase tracking-wider text-white/70"
              data-testid={`player-card-value-${player.slug}`}
            >
              {player.value_usd} / {player.value_kes}
              <span className="ml-2 text-white/40">{player.value_estimated ? "est." : "fee"}</span>
            </p>
          ) : null}
        </div>
      </Link>
    </motion.div>
  );
};
